import { db } from '../firebase/config.ts';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';

export type MarketingAppId = 'spontane' | 'emulsify';
export type MarketingRunDecision = 'pending' | 'accepted' | 'rejected' | 'needs_revision' | 'executed';
export type MarketingSyncStatus = 'ready' | 'queued' | 'running' | 'completed' | 'needs_setup' | 'error';

export interface MarketingSourceConfig {
  status: MarketingSyncStatus;
  note: string;
  trackedNiches: string[];
  trackedCreators: string[];
  trackedCompetitors: string[];
  trackedKeywords: string[];
}

export interface MarketingMeasurementEvent {
  id: string;
  label: string;
  whyItMatters: string;
}

export interface MarketingCreativeHypothesis {
  id: string;
  channel: 'TikTok' | 'Instagram Reels' | 'YouTube Shorts' | 'ASO' | 'Urban activation' | 'Paid test';
  hook: string;
  angle: string;
  metric: string;
}

export interface MarketingAppProfile {
  id: MarketingAppId;
  name: string;
  market: string;
  corePromise: string;
  primaryAudiences: string[];
  positioningProof: string[];
  rejectedClaims: string[];
  contentAngles: string[];
  virlo: MarketingSourceConfig;
  dataForSeo: MarketingSourceConfig;
  measurementEvents: MarketingMeasurementEvent[];
  creativeHypotheses: MarketingCreativeHypothesis[];
  weeklyLoop: string[];
  updatedAt?: string;
}

export interface MarketingSkillDefinition {
  id: string;
  label: string;
  sourceSkills: string[];
  description: string;
  defaultObjective: string;
  outputTemplate: string[];
}

export interface MarketingSkillRun {
  id: string;
  appId: MarketingAppId;
  skillId: string;
  skillLabel: string;
  objective: string;
  inputsUsed: string[];
  recommendation: string;
  nextActions: string[];
  measurementTarget: string;
  confidence: 'Low' | 'Medium' | 'High';
  decision: MarketingRunDecision;
  outcomeNotes: string;
  createdAt: string;
}

export interface MarketingSyncRun {
  id: string;
  appId: MarketingAppId | 'all';
  status: MarketingSyncStatus;
  sources: string[];
  requestedAt: string;
  summary: string;
  sourceDetails?: MarketingSyncSource[];
}

export interface MarketingSyncSource {
  appId?: MarketingAppId;
  name: string;
  status: MarketingSyncStatus;
  summary: string;
  keywords?: string[];
  sample?: unknown;
}

export interface MarketingAnalyticsEventCount {
  eventName: string;
  eventCount: number;
  activeUsers: number;
}

export interface MarketingCampaignAnalytics {
  sourceMedium: string;
  campaignName: string;
  eventCount: number;
  activeUsers: number;
  installs: number;
  activations: number;
  activationRate: number;
  events?: Record<string, number>;
}

export interface MarketingAnalyticsSnapshot {
  id: string;
  appId: MarketingAppId;
  status: MarketingSyncStatus;
  source: string;
  summary: string;
  syncedAt: string;
  dateRange: { start: string; end: string };
  propertyId?: string;
  setupHint?: string;
  totals: {
    installs: number;
    appOpens: number;
    activations: number;
    activationRate: number;
    campaignRows: number;
  };
  events: MarketingAnalyticsEventCount[];
  campaigns: MarketingCampaignAnalytics[];
}

export type MarketingVideoPlatform = 'TikTok' | 'Instagram Reels' | 'YouTube Shorts' | 'Other';

export interface MarketingPostedVideoMetricSnapshot {
  capturedAt: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
}

export interface MarketingPostedVideo {
  id: string;
  appId: MarketingAppId;
  platform: MarketingVideoPlatform;
  url: string;
  title: string;
  campaign: string;
  audio: string;
  hashtags: string[];
  postedAt: string;
  notes: string;
  snapshots: MarketingPostedVideoMetricSnapshot[];
  createdAt: string;
}

const PROFILE_COLLECTION = 'marketing_cockpit_profiles';
const SYNC_REQUESTS_COLLECTION = 'marketing_cockpit_sync_requests';
const SKILL_RUNS_COLLECTION = 'marketing_cockpit_skill_runs';
const ANALYTICS_COLLECTION = 'marketing_cockpit_analytics_snapshots';
const POSTED_VIDEOS_COLLECTION = 'marketing_cockpit_posted_videos';
const CLOUD_API_BASE = 'https://scraping-machine-rclzjt4gza-ew.a.run.app';

function nowIso(): string {
  return new Date().toISOString();
}

function dateFromUnknown(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  return nowIso();
}

function metricSnapshotFromRecord(value: unknown): MarketingPostedVideoMetricSnapshot {
  const rec = asRecord(value);
  return {
    capturedAt: dateFromUnknown(rec.capturedAt || rec.capturedAtIso),
    views: Number(rec.views || 0),
    likes: Number(rec.likes || 0),
    comments: Number(rec.comments || 0),
    shares: Number(rec.shares || 0),
    saves: Number(rec.saves || 0),
  };
}

export const MARKETING_SKILLS: MarketingSkillDefinition[] = [
  {
    id: 'product-marketing-context',
    label: 'Product Marketing Context',
    sourceSkills: ['product-marketing'],
    description: 'Clarifies audience, pain, promise, proof, objections, and brand tone.',
    defaultObjective: 'Update the product marketing context for this app before other skills run.',
    outputTemplate: ['Audience', 'Pain', 'Promise', 'Proof', 'Objections', 'Tone'],
  },
  {
    id: 'aso-audit',
    label: 'ASO Audit',
    sourceSkills: ['aso', 'copywriting', 'ab-testing'],
    description: 'Reviews store copy, screenshots, keywords, and competitor listings.',
    defaultObjective: 'Find app store listing improvements and tests for the next release cycle.',
    outputTemplate: ['Listing issue', 'Keyword opportunity', 'Screenshot idea', 'Test'],
  },
  {
    id: 'social-trend-translation',
    label: 'Social Trend Translation',
    sourceSkills: ['social', 'ad-creative', 'video'],
    description: 'Turns Virlo trend and outlier data into app-specific content hooks.',
    defaultObjective: 'Translate current short-form trends into content angles worth creating.',
    outputTemplate: ['Trend', 'Why it fits', 'Hook', 'Format', 'Ignore warning'],
  },
  {
    id: 'competitor-teardown',
    label: 'Competitor Teardown',
    sourceSkills: ['competitor-profiling', 'customer-research'],
    description: 'Explains competitor behavior, outliers, repeat patterns, and response ideas.',
    defaultObjective: 'Identify competitor content patterns CVL should copy, avoid, or test.',
    outputTemplate: ['Competitor pattern', 'Evidence', 'CVL response', 'Risk'],
  },
  {
    id: 'paid-test-planner',
    label: 'Paid Test Planner',
    sourceSkills: ['ads', 'ad-creative', 'ab-testing'],
    description: 'Turns accepted organic ideas into small paid experiments.',
    defaultObjective: 'Create a small paid test with budget, audience, creative, and stopping rule.',
    outputTemplate: ['Hypothesis', 'Budget', 'Audience', 'Creative', 'Success metric', 'Stopping rule'],
  },
  {
    id: 'analytics-check',
    label: 'Analytics Check',
    sourceSkills: ['analytics'],
    description: 'Checks whether campaigns connect clicks to installs, activation, and retention.',
    defaultObjective: 'Verify the measurement path before meaningful paid spend.',
    outputTemplate: ['Event', 'Source', 'Missing tracking', 'Fix'],
  },
  {
    id: 'experiment-designer',
    label: 'Experiment Designer',
    sourceSkills: ['ab-testing', 'marketing-plan'],
    description: 'Creates simple tests for hooks, audiences, captions, store assets, and landing pages.',
    defaultObjective: 'Design a clear marketing experiment with a hypothesis and decision rule.',
    outputTemplate: ['Hypothesis', 'Variants', 'Audience', 'Metric', 'Decision rule'],
  },
  {
    id: 'launch-relaunch-planner',
    label: 'Launch/Relaunch Planner',
    sourceSkills: ['launch', 'marketing-plan'],
    description: 'Plans weekly launch pushes and relaunch beats.',
    defaultObjective: 'Plan the next launch or relaunch push for this app.',
    outputTemplate: ['Moment', 'Audience', 'Message', 'Channels', 'Timeline'],
  },
  {
    id: 'onboarding-alignment',
    label: 'Onboarding Alignment',
    sourceSkills: ['onboarding', 'cro'],
    description: 'Checks whether campaign promises match the first app experience.',
    defaultObjective: 'Make sure acquisition promises lead to a matching activation moment.',
    outputTemplate: ['Promise', 'First-run moment', 'Friction', 'Fix'],
  },
  {
    id: 'weekly-postmortem',
    label: 'Weekly Postmortem',
    sourceSkills: ['analytics', 'marketing-plan', 'customer-research'],
    description: 'Compares plan, outcomes, learning, stop/continue decisions, and next tests.',
    defaultObjective: 'Summarize the week and choose what changes next week.',
    outputTemplate: ['Planned', 'Happened', 'Learned', 'Stop', 'Continue', 'Next test'],
  },
];

export const DEFAULT_MARKETING_PROFILES: Record<MarketingAppId, MarketingAppProfile> = {
  spontane: {
    id: 'spontane',
    name: 'Spontane',
    market: 'Israel first, with a Tel Aviv / urban discovery beachhead.',
    corePromise: 'Open the map and see what is happening around you tonight.',
    primaryAudiences: [
      'Tel Aviv young adults looking for last-minute plans',
      'Students and early-career locals who want cheap, spontaneous nights',
      'Expats and tourists who do not know local event pages',
      'Couples and friend groups looking for date-night or after-work ideas',
    ],
    positioningProof: [
      'AI aggregates events from across the web instead of relying on manual submissions.',
      'The live map format makes discovery immediate and local.',
      'Personal preferences tailor discovery instead of showing a generic event list.',
    ],
    rejectedClaims: [
      'Do not claim every event in Israel is covered.',
      'Do not imply tickets are sold inside the app unless that flow exists.',
      'Do not position Spontane as another static event calendar.',
    ],
    contentAngles: [
      'I was bored tonight',
      'Hidden events near me',
      'Date night rescue',
      'Things happening right now',
      'AI found this event before I did',
      'Weekend map check',
    ],
    virlo: {
      status: 'needs_setup',
      note: 'Connect Virlo Starter via MCP/API on the backend; track local creators, venues, city guides, nightlife pages, and event apps.',
      trackedNiches: ['Tel Aviv events', 'things to do Tel Aviv', 'nightlife Israel', 'date ideas Tel Aviv'],
      trackedCreators: ['telaviv', 'secret_telaviv', 'timeoutisrael'],
      trackedCompetitors: ['Eventbrite', 'Meetup', 'Facebook Events', 'Time Out Tel Aviv'],
      trackedKeywords: ['events tonight tel aviv', 'things to do tel aviv', 'date night tel aviv'],
    },
    dataForSeo: {
      status: 'needs_setup',
      note: 'Use DataForSEO for regional search demand and related queries around events, date ideas, nightlife, and city discovery.',
      trackedNiches: ['local discovery', 'events', 'nightlife', 'tourism'],
      trackedCreators: [],
      trackedCompetitors: ['Eventbrite', 'Meetup', 'Time Out'],
      trackedKeywords: ['things to do in tel aviv', 'events tel aviv tonight', 'tel aviv nightlife', 'date ideas tel aviv'],
    },
    measurementEvents: [
      { id: 'install', label: 'Install', whyItMatters: 'Top-of-funnel acquisition volume.' },
      { id: 'first_map_open', label: 'First map open', whyItMatters: 'Confirms the user reached the core product promise.' },
      { id: 'preference_setup', label: 'Preference setup completed', whyItMatters: 'Signals intent and enables personalization.' },
      { id: 'event_detail_view', label: 'Event detail view', whyItMatters: 'Shows discovery progressed beyond browsing.' },
      { id: 'save_share_navigation', label: 'Save, share, or navigation click', whyItMatters: 'Strong activation proxy for real-world intent.' },
      { id: 'd1_d7_retention', label: 'Day 1 / Day 7 retention', whyItMatters: 'Prevents optimizing only for low-quality installs.' },
    ],
    creativeHypotheses: [
      { id: 'sp-01', channel: 'TikTok', hook: 'I opened a map and found 5 things happening tonight in Tel Aviv.', angle: 'Immediate local discovery', metric: 'First map open per install' },
      { id: 'sp-02', channel: 'Instagram Reels', hook: 'Stop texting "what should we do tonight?"', angle: 'Friend group planning pain', metric: 'Install to event detail view' },
      { id: 'sp-03', channel: 'TikTok', hook: 'AI found this hidden event before my friends did.', angle: 'Novelty and insider feeling', metric: 'Shares per view' },
      { id: 'sp-04', channel: 'Urban activation', hook: 'Scan before you choose tonight.', angle: 'QR flyers near nightlife streets', metric: 'QR installs and first map opens' },
      { id: 'sp-05', channel: 'ASO', hook: 'Live map for events around you.', angle: 'Store clarity', metric: 'Store conversion rate' },
      { id: 'sp-06', channel: 'Paid test', hook: 'Date night in Tel Aviv, solved in 10 seconds.', angle: 'Couples and dating', metric: 'Cost per event detail view' },
      { id: 'sp-07', channel: 'Instagram Reels', hook: 'Three cheap plans happening near you this weekend.', angle: 'Budget-conscious weekend planning', metric: 'Saves per reach' },
      { id: 'sp-08', channel: 'TikTok', hook: 'Tourist in Tel Aviv? Open this before walking out.', angle: 'Tourist utility', metric: 'Install to first map open' },
      { id: 'sp-09', channel: 'Urban activation', hook: 'No plans? This QR has tonight’s map.', angle: 'Street-level curiosity', metric: 'QR scan to install' },
      { id: 'sp-10', channel: 'YouTube Shorts', hook: 'I checked what is happening around Dizengoff tonight.', angle: 'Neighborhood-specific map demo', metric: 'View to install click' },
      { id: 'sp-11', channel: 'TikTok', hook: 'The event apps missed this, but the map found it.', angle: 'Alternative to static event calendars', metric: 'Install conversion' },
      { id: 'sp-12', channel: 'Instagram Reels', hook: 'Before you say there is nothing to do, check this.', angle: 'Reframing boredom', metric: 'Profile clicks per reach' },
    ],
    weeklyLoop: [
      'Monday: sync Virlo and DataForSEO signals.',
      'Tuesday: run Social Trend Translation and Competitor Teardown.',
      'Wednesday: choose 3-5 content experiments and 1 local activation test.',
      'Friday: review weekend readiness and boost only strong organic posts.',
      'Sunday: run Weekly Postmortem and update next week priorities.',
    ],
  },
  emulsify: {
    id: 'emulsify',
    name: 'Emulsify',
    market: 'International cooking, food learning, and AI-assisted recipe experimentation.',
    corePromise: 'Your personal cooking brain, not just another recipe app.',
    primaryAudiences: [
      'Experimental home cooks who modify recipes',
      'People who save recipes but cannot organize what actually worked',
      'Food learners who want to understand technique and iteration',
      'AI-curious cooks and creators who want better cooking memory',
    ],
    positioningProof: [
      'Tracks real cooking sessions, not only polished recipes.',
      'Helps recipes evolve over time as the cook adapts and learns.',
      'Captures why changes worked so the user can repeat and improve.',
    ],
    rejectedClaims: [
      'Do not position Emulsify as only a recipe search app.',
      'Do not promise nutrition or health outcomes unless validated.',
      'Do not claim it replaces cooking judgment; it augments learning and memory.',
    ],
    contentAngles: [
      'I changed the recipe halfway through',
      'What I learned after making this four times',
      'My recipe finally remembers my changes',
      'Cooking notebook upgraded with AI',
      'Stop losing your best kitchen experiments',
      'From recipe chaos to personal cooking system',
    ],
    virlo: {
      status: 'needs_setup',
      note: 'Connect Virlo Starter via MCP/API; track cooking creators, recipe iterators, food educators, meal prep creators, and AI-food content.',
      trackedNiches: ['recipe experimentation', 'home cooking', 'AI cooking', 'cooking journal', 'meal prep'],
      trackedCreators: ['seriouseats', 'bonappetitmag', 'nytcooking'],
      trackedCompetitors: ['Paprika', 'Samsung Food', 'Notion recipe templates', 'Recipe Keeper'],
      trackedKeywords: ['ai recipe app', 'recipe organizer', 'cooking journal'],
    },
    dataForSeo: {
      status: 'needs_setup',
      note: 'Use DataForSEO for international search intent around recipe organization, AI cooking, cooking journals, and meal planning.',
      trackedNiches: ['recipe management', 'AI cooking', 'food learning', 'meal planning'],
      trackedCreators: [],
      trackedCompetitors: ['Paprika', 'Samsung Food', 'Recipe Keeper'],
      trackedKeywords: ['recipe organizer app', 'ai recipe app', 'cooking journal app', 'meal planning app', 'save recipes app'],
    },
    measurementEvents: [
      { id: 'install', label: 'Install', whyItMatters: 'Top-of-funnel acquisition volume.' },
      { id: 'first_recipe_session', label: 'First recipe/session created', whyItMatters: 'Confirms the user reached the core product promise.' },
      { id: 'recipe_edited', label: 'Recipe edited or evolved', whyItMatters: 'Shows the user values iteration, not only storage.' },
      { id: 'session_logged', label: 'Cooking session logged', whyItMatters: 'Strong activation signal for real cooking use.' },
      { id: 'return_session', label: 'Return cooking session', whyItMatters: 'Validates retention and repeated utility.' },
      { id: 'd1_d7_retention', label: 'Day 1 / Day 7 retention', whyItMatters: 'Prevents optimizing only for curiosity installs.' },
    ],
    creativeHypotheses: [
      { id: 'em-01', channel: 'TikTok', hook: 'I changed the recipe halfway through and my app remembered why.', angle: 'Real cooking adaptation', metric: 'First recipe/session created' },
      { id: 'em-02', channel: 'Instagram Reels', hook: 'Your recipe app should remember what actually happened.', angle: 'Gap between recipes and reality', metric: 'Install to session logged' },
      { id: 'em-03', channel: 'YouTube Shorts', hook: 'I made this dish 4 times. Here is what changed.', angle: 'Recipe evolution', metric: 'Watch completion to install' },
      { id: 'em-04', channel: 'ASO', hook: 'AI cooking journal for recipes that evolve.', angle: 'Store positioning clarity', metric: 'Store conversion rate' },
      { id: 'em-05', channel: 'Paid test', hook: 'Stop losing your best kitchen experiments.', angle: 'Pain of forgotten modifications', metric: 'Cost per first recipe/session' },
      { id: 'em-06', channel: 'TikTok', hook: 'This is not a recipe app. It is my cooking memory.', angle: 'Category reframing', metric: 'Profile clicks per reach' },
      { id: 'em-07', channel: 'Instagram Reels', hook: 'I never cook recipes exactly. This app was made for that.', angle: 'Identity fit for experimental cooks', metric: 'Install conversion' },
      { id: 'em-08', channel: 'YouTube Shorts', hook: 'What I would change next time, saved automatically.', angle: 'Learning loop', metric: 'Recipe edited per install' },
      { id: 'em-09', channel: 'TikTok', hook: 'From messy notes to a recipe I can actually repeat.', angle: 'Before/after organization', metric: 'Session logged per install' },
      { id: 'em-10', channel: 'Instagram Reels', hook: 'AI for people who actually cook, not just collect recipes.', angle: 'Anti-passive recipe collection', metric: 'Saves per reach' },
      { id: 'em-11', channel: 'Paid test', hook: 'Your personal cooking brain starts with one session.', angle: 'Activation-focused promise', metric: 'Cost per activated user' },
      { id: 'em-12', channel: 'TikTok', hook: 'I built a version history for dinner.', angle: 'Tech metaphor for cooks', metric: 'Comments per view' },
    ],
    weeklyLoop: [
      'Monday: sync Virlo and DataForSEO signals.',
      'Tuesday: run ASO Audit and Social Trend Translation.',
      'Wednesday: choose 3-5 creator/content experiments.',
      'Thursday: run Paid Test Planner for the strongest hook.',
      'Sunday: run Weekly Postmortem and update launch priorities.',
    ],
  },
};

function mergeProfile(appId: MarketingAppId, data: Partial<MarketingAppProfile> | undefined): MarketingAppProfile {
  const base = DEFAULT_MARKETING_PROFILES[appId];
  if (!data) return base;
  return {
    ...base,
    ...data,
    virlo: { ...base.virlo, ...(data.virlo || {}) },
    dataForSeo: { ...base.dataForSeo, ...(data.dataForSeo || {}) },
    measurementEvents: data.measurementEvents?.length ? data.measurementEvents : base.measurementEvents,
    creativeHypotheses: data.creativeHypotheses?.length ? data.creativeHypotheses : base.creativeHypotheses,
    weeklyLoop: data.weeklyLoop?.length ? data.weeklyLoop : base.weeklyLoop,
  };
}

export async function getMarketingProfile(appId: MarketingAppId): Promise<MarketingAppProfile> {
  try {
    const snap = await getDoc(doc(db, PROFILE_COLLECTION, appId));
    return mergeProfile(appId, snap.exists() ? (snap.data() as Partial<MarketingAppProfile>) : undefined);
  } catch (error) {
    console.warn('[marketing] failed loading profile, using defaults:', error);
    return DEFAULT_MARKETING_PROFILES[appId];
  }
}

export async function saveMarketingProfile(profile: MarketingAppProfile): Promise<void> {
  await setDoc(
    doc(db, PROFILE_COLLECTION, profile.id),
    {
      ...profile,
      updatedAt: nowIso(),
    },
    { merge: true },
  );
}

export async function requestMarketingSync(appId: MarketingAppId | 'all'): Promise<MarketingSyncRun> {
  try {
    const response = await fetch(`${CLOUD_API_BASE}/api/marketing/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appId }),
    });
    if (response.ok) {
      const data = await response.json();
      return {
        id: String(data.id || `api_${Date.now()}`),
        appId: (data.appId as MarketingAppId | 'all') || appId,
        status: (data.status as MarketingSyncStatus) || 'completed',
        sources: Array.isArray(data.sources) ? data.sources.map(String) : ['Virlo MCP/API', 'DataForSEO API'],
        requestedAt: String(data.requestedAtIso || nowIso()),
        summary: String(data.summary || 'Marketing sync completed.'),
      };
    }
  } catch (error) {
    console.warn('[marketing] backend sync unavailable, falling back to Firestore queue:', error);
  }

  const sources = ['Virlo MCP/API', 'DataForSEO API', 'marketingskills workflow context'];
  const payload = {
    appId,
    status: 'queued',
    sources,
    requestedAt: serverTimestamp(),
    requestedAtIso: nowIso(),
    summary:
      'Sync request queued. External API credentials should be handled by a backend worker; frontend stores request and reads results.',
  };
  const ref = await addDoc(collection(db, SYNC_REQUESTS_COLLECTION), payload);
  return {
    id: ref.id,
    appId,
    status: 'queued',
    sources,
    requestedAt: payload.requestedAtIso,
    summary: payload.summary,
  };
}

export async function getRecentMarketingSyncRuns(appId?: MarketingAppId | 'all'): Promise<MarketingSyncRun[]> {
  try {
    // Query only by timestamp and filter client-side to avoid requiring a Firestore composite index.
    const syncQuery = query(collection(db, SYNC_REQUESTS_COLLECTION), orderBy('requestedAt', 'desc'), limit(50));
    const snap = await getDocs(syncQuery);
    return snap.docs
      .map((d) => {
        const data = d.data();
        return {
        id: d.id,
        appId: (data.appId as MarketingAppId | 'all') || 'all',
        status: (data.status as MarketingSyncStatus) || 'queued',
        sources: Array.isArray(data.sources) ? data.sources.map(String) : [],
        requestedAt: dateFromUnknown(data.requestedAt || data.requestedAtIso),
        summary: String(data.summary || ''),
        sourceDetails: Array.isArray(data.sourceDetails) ? (data.sourceDetails as MarketingSyncSource[]) : [],
      };
      })
      .filter((run) => !appId || run.appId === appId || run.appId === 'all')
      .slice(0, 20);
  } catch (error) {
    console.warn('[marketing] failed loading sync runs:', error);
    return [];
  }
}

function analyticsSnapshotFromDoc(id: string, data: Record<string, unknown>): MarketingAnalyticsSnapshot {
  const totals = asRecord(data.totals);
  const dateRange = asRecord(data.dateRange);
  const events = Array.isArray(data.events) ? data.events.map((event) => {
    const rec = asRecord(event);
    return {
      eventName: String(rec.eventName || ''),
      eventCount: Number(rec.eventCount || 0),
      activeUsers: Number(rec.activeUsers || 0),
    };
  }) : [];
  const campaigns = Array.isArray(data.campaigns) ? data.campaigns.map((campaign) => {
    const rec = asRecord(campaign);
    return {
      sourceMedium: String(rec.sourceMedium || '(not set)'),
      campaignName: String(rec.campaignName || '(not set)'),
      eventCount: Number(rec.eventCount || 0),
      activeUsers: Number(rec.activeUsers || 0),
      installs: Number(rec.installs || 0),
      activations: Number(rec.activations || 0),
      activationRate: Number(rec.activationRate || 0),
      events: asRecord(rec.events) as Record<string, number>,
    };
  }) : [];

  return {
    id,
    appId: (data.appId as MarketingAppId) || 'spontane',
    status: (data.status as MarketingSyncStatus) || 'needs_setup',
    source: String(data.source || 'GA4/Firebase Analytics'),
    summary: String(data.summary || ''),
    syncedAt: dateFromUnknown(data.syncedAt || data.syncedAtIso),
    dateRange: {
      start: String(dateRange.start || '30daysAgo'),
      end: String(dateRange.end || 'today'),
    },
    propertyId: data.propertyId ? String(data.propertyId) : undefined,
    setupHint: data.setupHint ? String(data.setupHint) : undefined,
    totals: {
      installs: Number(totals.installs || 0),
      appOpens: Number(totals.appOpens || 0),
      activations: Number(totals.activations || 0),
      activationRate: Number(totals.activationRate || 0),
      campaignRows: Number(totals.campaignRows || campaigns.length),
    },
    events,
    campaigns,
  };
}

export async function getLatestMarketingAnalyticsSnapshot(appId: MarketingAppId): Promise<MarketingAnalyticsSnapshot | null> {
  try {
    const q = query(collection(db, ANALYTICS_COLLECTION), orderBy('syncedAt', 'desc'), limit(50));
    const snap = await getDocs(q);
    const snapshots = snap.docs
      .map((docSnap) => analyticsSnapshotFromDoc(docSnap.id, docSnap.data() as Record<string, unknown>))
      .filter((snapshot) => snapshot.appId === appId);
    return snapshots[0] || null;
  } catch (error) {
    console.warn('[marketing] failed loading analytics snapshot:', error);
    return null;
  }
}

export async function requestMarketingAnalyticsSync(appId: MarketingAppId | 'all'): Promise<MarketingAnalyticsSnapshot[]> {
  try {
    const response = await fetch(`${CLOUD_API_BASE}/api/marketing/analytics/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appId }),
    });
    if (response.ok) {
      const data = await response.json();
      return Array.isArray(data.appResults)
        ? data.appResults.map((item: unknown) => {
          const rec = asRecord(item);
          return analyticsSnapshotFromDoc(String(rec.id || `api_${Date.now()}`), rec);
        })
        : [];
    }
  } catch (error) {
    console.warn('[marketing] analytics backend sync unavailable:', error);
  }
  return [];
}

async function getLatestCompletedMarketingSync(appId: MarketingAppId): Promise<MarketingSyncRun | null> {
  const runs = await getRecentMarketingSyncRuns(appId);
  return runs.find((run) => run.status === 'completed' && run.sourceDetails?.length) || null;
}

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object' ? (value as Record<string, any>) : {};
}

function summarizeDataForSeo(source?: MarketingSyncSource): { evidence: string[]; keywords: string[]; topKeywords: string[] } {
  if (!source) {
    return { evidence: ['DataForSEO: no completed sync payload found.'], keywords: [], topKeywords: [] };
  }

  const sample = asRecord(source.sample);
  const tasks = Array.isArray(sample.tasks) ? sample.tasks : [];
  const firstTask = asRecord(tasks[0]);
  const taskData = asRecord(firstTask.data);
  const result = Array.isArray(firstTask.result) ? firstTask.result : [];
  const firstResult = asRecord(result[0]);
  const items = Array.isArray(firstResult.items) ? firstResult.items : [];
  const graph = asRecord(items.find((item: unknown) => asRecord(item).type === 'google_trends_graph') || items[0]);
  const keywords = (source.keywords?.length ? source.keywords : taskData.keywords || firstResult.keywords || graph.keywords || [])
    .map(String);
  const averages = Array.isArray(graph.averages) ? graph.averages : [];
  const ranked = keywords
    .map((keyword, index) => ({ keyword, score: Number(averages[index] || 0) }))
    .sort((a, b) => b.score - a.score);
  const topKeywords = ranked.filter((item) => item.score > 0).map((item) => item.keyword);
  const checkUrl = String(firstResult.check_url || '');
  const evidence = [
    `DataForSEO completed for keywords: ${keywords.join(', ') || 'none returned'}.`,
    topKeywords.length
      ? `Highest observed Google Trends averages: ${ranked.filter((item) => item.score > 0).map((item) => `${item.keyword} (${item.score})`).join(', ')}.`
      : 'Google Trends returned little or no measurable volume for these exact terms; broaden or localize keywords.',
    checkUrl ? `Trend comparison URL available from DataForSEO.` : 'No trend comparison URL returned.',
  ];
  return { evidence, keywords, topKeywords };
}

function summarizeVirlo(source?: MarketingSyncSource): { evidence: string[]; hasItems: boolean } {
  if (!source) {
    return { evidence: ['Virlo: no completed sync payload found.'], hasItems: false };
  }

  const sample = asRecord(source.sample);
  const videos = Array.isArray(sample.videos) ? sample.videos : [];
  const trends = Array.isArray(sample.trends) ? sample.trends : [];
  const outliers = Array.isArray(sample.creator_outliers) ? sample.creator_outliers : [];
  const digestData = Array.isArray(sample.data) ? sample.data : [];
  const data = videos.length ? videos : digestData;
  if (!data.length && !trends.length && !outliers.length) {
    return {
      evidence: [
        sample.orbit_id
          ? `Virlo Orbit ${sample.orbit_id} connected, but no videos/trends were returned yet.`
          : 'Virlo connected successfully, but no content items were returned for this app.',
        source.status === 'running'
          ? 'The Orbit job is still running; run sync again in a few minutes to collect completed evidence.'
          : 'Treat this as a setup signal: broaden keywords or refresh the app-specific Virlo Content Research Agent before relying on competitor evidence.',
      ],
      hasItems: false,
    };
  }

  const firstVideos = data.slice(0, 3).map((item: unknown, index: number) => {
    const rec = asRecord(item);
    const author = asRecord(rec.author);
    const creator = author.username ? ` by ${author.username}` : '';
    const views = typeof rec.views === 'number' ? ` (${rec.views.toLocaleString()} views)` : '';
    return rec.description || rec.title || rec.url
      ? `${String(rec.description || rec.title || rec.url).slice(0, 80)}${creator}${views}`
      : `Virlo video ${index + 1}`;
  });
  const firstTrends = trends.slice(0, 3).map((item: unknown) => {
    const rec = asRecord(item);
    const label = rec.title || rec.name || rec.trend || rec.summary || rec.description || 'Unnamed trend';
    const views = typeof rec.total_views === 'number' ? ` (${rec.total_views.toLocaleString()} total views)` : '';
    return `${label}${views}`;
  });
  const firstOutliers = outliers.slice(0, 3).map((item: unknown) => {
    const rec = asRecord(item);
    const username = rec.username || rec.name || asRecord(rec.creator).username || 'unknown creator';
    const score = rec.outlier_ratio || rec.weighted_score;
    return score ? `${username} (${score} outlier score)` : String(username);
  });

  const evidence = [
    `Virlo Orbit returned ${sample.total_videos || data.length} videos${trends.length ? `, ${trends.length} trends` : ''}${outliers.length ? `, ${outliers.length} creator outliers` : ''}.`,
  ];
  if (sample.analysis) evidence.push(`Virlo analysis: ${sample.analysis}`);
  if (firstTrends.length) evidence.push(`Top trend signals: ${firstTrends.join(' | ')}.`);
  if (firstVideos.length) evidence.push(`Top evidence videos: ${firstVideos.join(' | ')}.`);
  if (firstOutliers.length) evidence.push(`Creator outliers: ${firstOutliers.join(', ')}.`);

  return {
    evidence,
    hasItems: true,
  };
}

function buildEvidenceRecommendation(
  profile: MarketingAppProfile,
  skill: MarketingSkillDefinition,
  objective: string,
  latestSync: MarketingSyncRun | null,
): Pick<MarketingSkillRun, 'inputsUsed' | 'recommendation' | 'nextActions' | 'measurementTarget' | 'confidence'> {
  const details = latestSync?.sourceDetails || [];
  const virloSource = details.find((source) => source.name === 'Virlo MCP/API');
  const seoSource = details.find((source) => source.name === 'DataForSEO API');
  const virlo = summarizeVirlo(virloSource);
  const seo = summarizeDataForSeo(seoSource);
  const topKeywords = seo.topKeywords.length ? seo.topKeywords : seo.keywords.slice(0, 3);
  const primaryKeyword = topKeywords[0] || profile.dataForSeo.trackedKeywords[0] || profile.contentAngles[0];
  const fallbackAngle = profile.contentAngles[0];
  const secondAngle = profile.contentAngles[1] || fallbackAngle;
  const topMetric = profile.measurementEvents[1]?.label || 'Activation';
  const sourceFreshness = latestSync ? `latest sync from ${formatSourceDate(latestSync.requestedAt)}` : 'no completed sync';

  const inputsUsed = [
    `${profile.name} brand memory`,
    `Virlo sync evidence (${sourceFreshness})`,
    `DataForSEO keyword evidence (${sourceFreshness})`,
    `${skill.sourceSkills.join(', ')} skill framework`,
  ];

  let recommendation: string;
  let nextActions: string[];

  if (skill.id === 'competitor-teardown') {
    recommendation = [
      `Evidence-based competitor teardown for ${profile.name}:`,
      `Evidence: ${[...virlo.evidence, ...seo.evidence].join(' ')}`,
      virlo.hasItems
        ? `Pattern to inspect: compare the Virlo outliers against ${profile.virlo.trackedCompetitors.slice(0, 3).join(', ')}.`
        : `Current competitor evidence gap: Virlo has connectivity but no digest items yet, so do not claim a competitor pattern until the Virlo agent returns posts.`,
      `Search-demand proxy: use "${primaryKeyword}" as the strongest current market clue.`,
      `CVL response to test: create a ${profile.name} content piece around "${primaryKeyword}" with the angle "${fallbackAngle}".`,
      `Risk: ${profile.rejectedClaims[0]}`,
    ].join('\n\n');
    nextActions = [
      `In Virlo, confirm the ${profile.name} Content Research Agent is tracking: ${profile.virlo.trackedNiches.join(', ')}.`,
      `Add or verify competitors/accounts: ${profile.virlo.trackedCompetitors.join(', ')}.`,
      `Create one content test around "${primaryKeyword}" and one around "${secondAngle}".`,
      `Mark this run Executed only after you have posted or briefed the content.`,
    ];
  } else if (skill.id === 'social-trend-translation') {
    recommendation = [
      `Evidence-based social direction for ${profile.name}:`,
      `Evidence: ${[...virlo.evidence, ...seo.evidence].join(' ')}`,
      `Hook 1: "${makeHook(profile, primaryKeyword, 1)}"`,
      `Hook 2: "${makeHook(profile, secondAngle, 2)}"`,
      `Hook 3: "${makeHook(profile, topKeywords[1] || fallbackAngle, 3)}"`,
      `Format: native short-form video with screen recording or real-world context, not polished brand ad creative.`,
      `Ignore warning: skip broad generic lifestyle trends until Virlo returns app-relevant outlier posts.`,
    ].join('\n\n');
    nextActions = [
      `Produce 3 short videos using the hooks above.`,
      `Use "${primaryKeyword}" in caption or on-screen text where natural.`,
      `Track ${topMetric} by source before boosting anything.`,
      `Run Weekly Postmortem after the first posts have results.`,
    ];
  } else if (skill.id === 'aso-audit') {
    recommendation = [
      `Evidence-based ASO direction for ${profile.name}:`,
      `Evidence: ${seo.evidence.join(' ')}`,
      `Primary keyword candidate: "${primaryKeyword}".`,
      `Store-message test: lead with "${profile.corePromise}" and support it with keyword language around "${primaryKeyword}".`,
      `Screenshot test: show the activation moment connected to ${topMetric}.`,
    ].join('\n\n');
    nextActions = [
      `Draft one title/subtitle variant using "${primaryKeyword}".`,
      `Draft one screenshot headline using "${profile.contentAngles[0]}".`,
      `Compare against ${profile.dataForSeo.trackedCompetitors.slice(0, 3).join(', ')} before changing store copy.`,
    ];
  } else if (skill.id === 'paid-test-planner') {
    recommendation = [
      `Evidence-based paid test for ${profile.name}:`,
      `Evidence: ${seo.evidence.join(' ')}`,
      `Hypothesis: users searching or reacting to "${primaryKeyword}" will activate better than generic app-interest audiences.`,
      `Creative: use "${makeHook(profile, primaryKeyword, 1)}".`,
      `Budget: start tiny; do not scale until ${topMetric} is measurable.`,
    ].join('\n\n');
    nextActions = [
      `Create one Meta/TikTok creative from the hook.`,
      `Set success metric to ${topMetric}, not just installs.`,
      `Stop the test if clicks do not convert into ${topMetric}.`,
    ];
  } else {
    recommendation = [
      `Evidence-based ${skill.label} for ${profile.name}:`,
      `Objective: ${objective || skill.defaultObjective}`,
      `Evidence: ${[...virlo.evidence, ...seo.evidence].join(' ')}`,
      `Recommendation: anchor this workflow on "${profile.corePromise}", use "${primaryKeyword}" as current demand evidence, and avoid "${profile.rejectedClaims[0]}".`,
    ].join('\n\n');
    nextActions = [
      `Apply the ${skill.sourceSkills.join(' + ')} framework to the evidence above.`,
      `Create one output package using: ${skill.outputTemplate.join(', ')}.`,
      `Measure against ${topMetric}.`,
    ];
  }

  return {
    inputsUsed,
    recommendation,
    nextActions,
    measurementTarget: topMetric,
    confidence: latestSync && seo.keywords.length ? (virlo.hasItems ? 'High' : 'Medium') : 'Low',
  };
}

function formatSourceDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function makeHook(profile: MarketingAppProfile, seed: string, variant: number): string {
  if (profile.id === 'spontane') {
    const hooks = [
      `I checked ${seed} and found what to do tonight in Tel Aviv`,
      `Stop texting "what should we do tonight?" and open the map`,
      `Before you say there is nothing happening, check this live map`,
    ];
    return hooks[(variant - 1) % hooks.length];
  }
  const hooks = [
    `I changed the recipe and my cooking brain remembered why`,
    `Stop losing your best ${seed} experiments`,
    `This is not another recipe app, it is my cooking memory`,
  ];
  return hooks[(variant - 1) % hooks.length];
}

function buildRecommendation(profile: MarketingAppProfile, skill: MarketingSkillDefinition, objective: string): Pick<
  MarketingSkillRun,
  'inputsUsed' | 'recommendation' | 'nextActions' | 'measurementTarget' | 'confidence'
> {
  const inputsUsed = [
    `${profile.name} brand memory`,
    'Virlo tracked niches and competitors',
    'DataForSEO tracked keywords',
    `${skill.sourceSkills.join(', ')} skill framework`,
  ];

  const topAngles = profile.contentAngles.slice(0, 3).join(', ');
  const topKeywords = profile.dataForSeo.trackedKeywords.slice(0, 3).join(', ');
  const topMetric = profile.measurementEvents[1]?.label || 'Activation';

  const recommendation = [
    `Use the ${skill.label} workflow for ${profile.name}.`,
    `Current objective: ${objective || skill.defaultObjective}`,
    `Anchor the work on the promise "${profile.corePromise}" and prioritize angles around ${topAngles}.`,
    `Use search demand around ${topKeywords || 'core category keywords'} and social outliers from Virlo as inputs before creating content or paid tests.`,
  ].join(' ');

  const nextActions = [
    `Refresh ${profile.name} Virlo/DataForSEO signals.`,
    `Apply the ${skill.sourceSkills.join(' + ')} framework.`,
    `Create one recommendation package using: ${skill.outputTemplate.join(', ')}.`,
    `Review as CEO and mark the result accepted, rejected, needs revision, or executed.`,
  ];

  return {
    inputsUsed,
    recommendation,
    nextActions,
    measurementTarget: topMetric,
    confidence: profile.virlo.status === 'ready' && profile.dataForSeo.status === 'ready' ? 'High' : 'Medium',
  };
}

export async function runMarketingSkill(
  appId: MarketingAppId,
  skillId: string,
  objective?: string,
): Promise<MarketingSkillRun> {
  const profile = await getMarketingProfile(appId);
  const skill = MARKETING_SKILLS.find((s) => s.id === skillId) || MARKETING_SKILLS[0];
  const resolvedObjective = objective?.trim() || skill.defaultObjective;
  const latestSync = await getLatestCompletedMarketingSync(appId);
  const generated = latestSync
    ? buildEvidenceRecommendation(profile, skill, resolvedObjective, latestSync)
    : buildRecommendation(profile, skill, resolvedObjective);
  const payload = {
    appId,
    skillId: skill.id,
    skillLabel: skill.label,
    objective: resolvedObjective,
    ...generated,
    decision: 'pending' as MarketingRunDecision,
    outcomeNotes: '',
    createdAt: serverTimestamp(),
    createdAtIso: nowIso(),
  };
  const ref = await addDoc(collection(db, SKILL_RUNS_COLLECTION), payload);
  return {
    id: ref.id,
    appId,
    skillId: skill.id,
    skillLabel: skill.label,
    objective: resolvedObjective,
    ...generated,
    decision: 'pending',
    outcomeNotes: '',
    createdAt: payload.createdAtIso,
  };
}

export async function getMarketingSkillRuns(appId: MarketingAppId): Promise<MarketingSkillRun[]> {
  try {
    const runsQuery = query(
      collection(db, SKILL_RUNS_COLLECTION),
      where('appId', '==', appId),
      orderBy('createdAt', 'desc'),
      limit(30),
    );
    const snap = await getDocs(runsQuery);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        appId: data.appId as MarketingAppId,
        skillId: String(data.skillId || ''),
        skillLabel: String(data.skillLabel || ''),
        objective: String(data.objective || ''),
        inputsUsed: Array.isArray(data.inputsUsed) ? data.inputsUsed.map(String) : [],
        recommendation: String(data.recommendation || ''),
        nextActions: Array.isArray(data.nextActions) ? data.nextActions.map(String) : [],
        measurementTarget: String(data.measurementTarget || ''),
        confidence: (data.confidence as 'Low' | 'Medium' | 'High') || 'Medium',
        decision: (data.decision as MarketingRunDecision) || 'pending',
        outcomeNotes: String(data.outcomeNotes || ''),
        createdAt: dateFromUnknown(data.createdAt || data.createdAtIso),
      };
    });
  } catch (error) {
    console.warn('[marketing] failed loading skill runs:', error);
    return [];
  }
}

export async function updateMarketingSkillDecision(
  run: MarketingSkillRun,
  decision: MarketingRunDecision,
  outcomeNotes?: string,
): Promise<void> {
  await setDoc(
    doc(db, SKILL_RUNS_COLLECTION, run.id),
    {
      decision,
      outcomeNotes: outcomeNotes ?? run.outcomeNotes ?? '',
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function getMarketingPostedVideos(appId: MarketingAppId): Promise<MarketingPostedVideo[]> {
  try {
    const videosQuery = query(
      collection(db, POSTED_VIDEOS_COLLECTION),
      where('appId', '==', appId),
      orderBy('postedAt', 'desc'),
      limit(30),
    );
    const snap = await getDocs(videosQuery);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        appId: data.appId as MarketingAppId,
        platform: (data.platform as MarketingVideoPlatform) || 'Other',
        url: String(data.url || ''),
        title: String(data.title || ''),
        campaign: String(data.campaign || ''),
        audio: String(data.audio || ''),
        hashtags: Array.isArray(data.hashtags) ? data.hashtags.map(String) : [],
        postedAt: dateFromUnknown(data.postedAt || data.postedAtIso),
        notes: String(data.notes || ''),
        snapshots: Array.isArray(data.snapshots) ? data.snapshots.map(metricSnapshotFromRecord) : [],
        createdAt: dateFromUnknown(data.createdAt || data.createdAtIso),
      };
    });
  } catch (error) {
    console.warn('[marketing] failed loading posted videos:', error);
    return [];
  }
}

export async function createMarketingPostedVideo(input: {
  appId: MarketingAppId;
  platform: MarketingVideoPlatform;
  url: string;
  title: string;
  campaign?: string;
  audio?: string;
  hashtags?: string[];
  postedAt?: string;
  notes?: string;
  snapshot?: Omit<MarketingPostedVideoMetricSnapshot, 'capturedAt'>;
}): Promise<MarketingPostedVideo> {
  const createdAtIso = nowIso();
  const postedAtIso = input.postedAt || createdAtIso;
  const snapshots = input.snapshot
    ? [{
      ...input.snapshot,
      capturedAt: createdAtIso,
    }]
    : [];
  const payload = {
    appId: input.appId,
    platform: input.platform,
    url: input.url.trim(),
    title: input.title.trim(),
    campaign: input.campaign?.trim() || '',
    audio: input.audio?.trim() || '',
    hashtags: input.hashtags || [],
    postedAt: input.postedAt ? Timestamp.fromDate(new Date(input.postedAt)) : serverTimestamp(),
    postedAtIso,
    notes: input.notes?.trim() || '',
    snapshots,
    createdAt: serverTimestamp(),
    createdAtIso,
    updatedAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, POSTED_VIDEOS_COLLECTION), payload);
  return {
    id: ref.id,
    appId: input.appId,
    platform: input.platform,
    url: payload.url,
    title: payload.title,
    campaign: payload.campaign,
    audio: payload.audio,
    hashtags: payload.hashtags,
    postedAt: postedAtIso,
    notes: payload.notes,
    snapshots,
    createdAt: createdAtIso,
  };
}

export async function addMarketingPostedVideoSnapshot(
  videoId: string,
  snapshot: Omit<MarketingPostedVideoMetricSnapshot, 'capturedAt'>,
): Promise<MarketingPostedVideoMetricSnapshot> {
  const capturedAt = nowIso();
  const payload = {
    capturedAt,
    views: Number(snapshot.views || 0),
    likes: Number(snapshot.likes || 0),
    comments: Number(snapshot.comments || 0),
    shares: Number(snapshot.shares || 0),
    saves: Number(snapshot.saves || 0),
  };
  await setDoc(
    doc(db, POSTED_VIDEOS_COLLECTION, videoId),
    {
      snapshots: arrayUnion(payload),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  return payload;
}

export async function seedMarketingProfiles(): Promise<MarketingAppProfile[]> {
  const profiles = await Promise.all(
    (Object.keys(DEFAULT_MARKETING_PROFILES) as MarketingAppId[]).map(async (appId) => {
      const profile = await getMarketingProfile(appId);
      await saveMarketingProfile(profile);
      return profile;
    }),
  );
  return profiles;
}

export const marketingCockpitService = {
  getMarketingProfile,
  saveMarketingProfile,
  requestMarketingSync,
  getRecentMarketingSyncRuns,
  runMarketingSkill,
  getMarketingSkillRuns,
  updateMarketingSkillDecision,
  getMarketingPostedVideos,
  createMarketingPostedVideo,
  addMarketingPostedVideoSnapshot,
  seedMarketingProfiles,
};

export default marketingCockpitService;
